/* eslint-disable max-lines */
import { useAtom } from "jotai";
import { Download, FolderOpen, Loader2, LogIn, Upload } from "lucide-react";
import { useEffect, useState } from "react";

import { useArtworkForm, useDtfArtworksInfinite } from "@design-lab/entities/artwork";
import { useProductColors } from "@design-lab/entities/product";
import { useDesignLibrarySelection } from "@design-lab/widgets/design-lab-upload-artwork";
import type { Side } from "@design-lab/shared";
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";
import { useDebounce } from "@design-lab/shared/hooks/useDebounce";
import { useInfiniteScroll } from "@design-lab/shared/hooks/useInfiniteScroll";
import { isUserLoggedIn } from "@design-lab/shared/lib/auth";
import { downloadFile } from "@design-lab/shared/lib/file";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { usePlacementPosition } from "@design-lab/widgets/artboard/model/usePlacementPosition";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import { redirectTo } from "common/utils/browser";
import { libraryStateAtom, MENU_IDS } from "../../model/state";
import { SHIRT_TAB_IDS } from "../../model/types/TabTypes";
import { useArtworkSelection } from "../../model/useArtworkSelection";
import { useMenuState } from "../../model/useMenuState";
import { SearchBar, type SortOption } from "./SearchBar";
import { ImageGrid } from "./ImageGrid";
import { ImageGridItem } from "./ImageGridItem";

type LibraryContentProps = {
  currentSide: Side;
};

export function LibraryContent({ currentSide }: LibraryContentProps) {
  const [libraryState, setLibraryState] = useAtom(libraryStateAtom);
  const [dtfChannelUuid, setDtfChannelUuid] = useState<string>("");
  const { setSelection, getActiveSelection } = useArtworkSelection();
  const { handleMenuChange } = useMenuState();
  const currentState = libraryState[currentSide];
  const { search, sort } = currentState;
  const activeSelection = getActiveSelection(currentSide);

  // Debounce the search to prevent API calls on every keystroke
  const debouncedSearch = useDebounce(search, 500);

  const channel = useDtfChannel();
  const { lens } = useArtworkForm();
  const { currentProduct } = useProductColors();

  const { placement: selectedFrontPlacement } = usePlacementPosition(
    lens.focus("selectedFrontPlacement")
  );

  // Determine placement based on current active tab
  const placement =
    currentSide === SHIRT_TAB_IDS.BACK ? SHIRT_TAB_IDS.BACK : selectedFrontPlacement;
  const isLoggedIn = isUserLoggedIn();

  const { selectArtwork } = useDesignLibrarySelection({
    placement
  });

  useEffect(() => {
    const fetchChannelUuid = async () => {
      const uuid = await channel.retrieveDtfChannelUuid();
      setDtfChannelUuid(uuid || "");
    };

    fetchChannelUuid();
  }, [channel]);

  // Check if we're waiting for catalog number
  const isWaitingForCatalogNumber = !currentProduct?.catalogNumber;

  const {
    data,
    isLoading: isLoadingDtfArtworks,
    isFetchingNextPage: isLoadingMore,
    hasNextPage: hasMore,
    error,
    fetchNextPage: loadMore
  } = useDtfArtworksInfinite({
    search: debouncedSearch,
    sortOrder: sort === "newest" ? "desc" : "asc",
    artworksType: "uploaded",
    dtfChannelUuid,
    catalogNumber: STANDARD_DTF_CATALOG_NUMBER,
    perPage: 20
  });

  const dtfArtworks = data?.pages?.flatMap(page => page?.artworks || []) ?? [];

  const isSearchPending = search !== debouncedSearch && search.trim() !== "";
  const enableInfiniteScroll = !isSearchPending && !isLoadingDtfArtworks;

  const { targetRef } = useInfiniteScroll({
    hasMore: !!hasMore,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    enabled: enableInfiniteScroll
  });

  const renderContent = () => {
    if (isWaitingForCatalogNumber || isLoadingDtfArtworks) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Loader2 className="size-8 animate-spin" />
          <p className="text-sm text-gray-600">
            {isWaitingForCatalogNumber ? "Loading product information..." : "Loading designs..."}
          </p>
        </div>
      );
    }

    if (error && !isLoadingDtfArtworks) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <FolderOpen className="size-8 text-gray-400" />
          <p className="text-sm text-gray-600">Failed to load designs</p>
        </div>
      );
    }

    if (!isLoadingDtfArtworks && !error && dtfArtworks.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <FolderOpen className="size-8 text-gray-400" />
          <p className="text-sm text-gray-600">
            {debouncedSearch
              ? `No designs found matching "${debouncedSearch}"`
              : "No designs in your library"}
          </p>
        </div>
      );
    }

    // Show content with infinite scroll
    return (
      <>
        <ImageGrid>
          {dtfArtworks.map(artwork => {
            const isSelected =
              activeSelection.activeSource === MENU_IDS.LIBRARY &&
              activeSelection.selectedId === artwork.id;
            return (
              <ImageGridItem
                key={artwork.id}
                imageUrl={artwork.original.imageUrl}
                imageName={artwork.fileName}
                isSelected={isSelected}
                borderColor="gray"
                onSelect={() => onSelectDesign(artwork.id)}
                actionButton={
                  <Button
                    data-testid={`download-artwork-${artwork.id}-button`}
                    variant="outline"
                    size="icon"
                    className="bg-white/90 backdrop-blur-sm"
                    onPress={() => downloadFile(artwork.original.imageUrl, artwork.fileName)}
                  >
                    <Download className="size-4" />
                  </Button>
                }
                metadata={
                  <>
                    <p className="truncate font-medium">{artwork.fileName}</p>
                    <p className="text-xs text-gray-500">
                      Size: {artwork.original.width} x {artwork.original.height}
                    </p>
                    <p className="text-xs text-gray-500">Uploaded: {artwork.uploadedOn}</p>
                  </>
                }
              />
            );
          })}
        </ImageGrid>

        {hasMore && (
          <div ref={targetRef} className="flex justify-center py-4">
            {isLoadingMore && <Loader2 className="size-6 animate-spin text-gray-400" />}
          </div>
        )}
      </>
    );
  };

  const onSelectDesign = (artworkId: number) => {
    if (
      activeSelection.activeSource === MENU_IDS.LIBRARY &&
      activeSelection.selectedId === artworkId
    ) {
      return;
    }
    // Update global selection state and clear other menu selections
    setSelection(currentSide, MENU_IDS.LIBRARY, artworkId);

    // Call the existing selectArtwork function
    selectArtwork(artworkId);
  };

  const updateLibraryState = (field: keyof typeof currentState, value: string) => {
    setLibraryState(prev => ({
      ...prev,
      [currentSide]: {
        ...prev[currentSide],
        [field]: value
      }
    }));
  };

  const handleSearchChange = (value: string) => updateLibraryState("search", value);
  const handleSortChange = (value: string) => updateLibraryState("sort", value);

  const sortOptions: SortOption[] = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" }
  ];

  // Show login prompt if user is not logged in
  if (!isLoggedIn) {
    return (
      <div className="flex size-full flex-1 flex-col items-center justify-center gap-6 p-8">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-gray-100">
            <LogIn className="size-8 text-gray-400" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Login Required</h3>
            <p className="max-w-sm text-sm text-gray-600">
              Please log in to access your personal design library and view your uploaded artwork.
            </p>
          </div>
          <Button
            className="bg-cta-gradient px-6 py-2 font-medium text-white hover:opacity-90"
            data-testid="sign-in-button"
            onPress={() => redirectTo("/login")}
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (dtfArtworks.length === 0 && !isLoadingDtfArtworks && !isWaitingForCatalogNumber) {
    return (
      <div className="flex size-full flex-1 flex-col items-center justify-center gap-6 p-8">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-gray-100">
            <FolderOpen className="size-8 text-gray-400" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">Your Library is Empty</h3>
            <p className="max-w-sm text-sm text-gray-600">
              {debouncedSearch
                ? `No designs found matching "${debouncedSearch}". Try adjusting your search terms or upload new artwork.`
                : "Start building your design library by uploading your first artwork. All your designs will appear here for easy access."}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid="upload-artwork-button"
              className="bg-cta-gradient px-6 py-2 font-medium text-white hover:opacity-90"
              onPress={() => handleMenuChange(MENU_IDS.UPLOAD_IMPORT)}
            >
              <Upload className="mr-2 size-4" />
              Upload Artwork
            </Button>
            {debouncedSearch && (
              <Button
                data-testid="clear-search-button"
                variant="outline"
                onPress={() => handleSearchChange("")}
                className="px-6 py-2"
              >
                Clear Search
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col gap-4 overflow-hidden">
      <SearchBar
        searchValue={search}
        onSearchChange={handleSearchChange}
        sortValue={sort}
        onSortChange={handleSortChange}
        sortOptions={sortOptions}
        searchPlaceholder="Search image names"
        searchTestId="search-input"
        sortTestId="sort-select-trigger"
        clearSearchTestId="library-clear-search"
        expandSearchTestId="library-expand-search"
        closeSearchTestId="library-close-search"
      />

      <div className="h-full overflow-y-auto">{renderContent()}</div>
    </div>
  );
}
